import * as React from 'react';
import { connect, DispatchProp } from 'react-redux';
import { actions } from 'react-redux-form';
import { toast } from 'react-toastify';

import { emptyPlantOptions, removePlantOptions, savePlantOptions } from '../../actions/miscellaneousActions';
import { EMPTY_FIELD_MESSAGE } from '../../constants/generals';
import { ISelectOptions } from '../../interfaces';
import { PlantModel } from '../../model/PlantModel';
import { UserModel } from '../../model/UserModel';
import { getAllPlantsByRegion, getPlantsByRegion } from '../../services/plantService';
import { dispatch, isFormEmpty } from '../../utils/generalUtils';
import { Async } from '../reusableComponents/Async';
import { ErrorPage } from '../reusableComponents/ErrorPage';
import { Form } from '../reusableComponents/FormComponents/Form';
import { FormFooter } from '../reusableComponents/FormComponents/FormFooter';
import { RRFInput } from '../reusableComponents/FormComponents/RRFInput';
import { Loader } from '../reusableComponents/Loader';

export interface IUserFormProps extends DispatchProp<any> {
    formModelName: string;
    plantOptions?: {
        Plant_Name: string
        PlantId: string
        UniqueId: string
        id: string
        Region: string
    }[];
    id: string;
    userId?: string;
    userInstance?: UserModel;
    onSubmit: (values) => void;
}

export interface IUserFormState {
    isNotL1User: boolean;
    plantDetails: {
        label: string;
        value: string
    }[];
    isLoading: boolean;
    plantIds: string[];
    plantOptions: ISelectOptions[];
}

export class UserFormImpl extends React.Component<IUserFormProps, IUserFormState> {
    constructor(props: IUserFormProps) {
        super(props);
        this.state = {
            isNotL1User: (props.userInstance && props.userInstance.props.Access_Level !== 'L1') ? true : false,
            plantDetails: [], plantIds: [], plantOptions: [], isLoading: false
        };
    }

    static defaultProps: IUserFormProps;

    handleFormSubmit = async (value) => {
        if (isFormEmpty(value)) {
            toast.error(EMPTY_FIELD_MESSAGE, {
                position: toast.POSITION.TOP_CENTER,
                className: 'failed-toast'
            });
            return;
        }
        return this.props.onSubmit(value);
    }

    getPlantOptions = (plantIds: string[] = []) => {
        const plantOptions = [];
        const uniquePlantIds = [];
        (plantIds || []).forEach(id => {
            const plantInstance = PlantModel.getBy('PlantId', id);
            if (plantInstance) {
                plantOptions.push({
                    label: plantInstance.props.Plant_Name,
                    value: plantInstance.props.UniqueId || plantInstance.props.id
                });
                uniquePlantIds.push(
                    plantInstance.props.UniqueId || plantInstance.props.id
                );
            }
        });
        if ((this.props.plantOptions || this.state.plantOptions || []).length > 0) {
            (this.props.plantOptions || []).forEach(instance => {
                plantOptions.push({
                    label: instance.Plant_Name,
                    value: instance.UniqueId || instance.id
                });
            });
        }
        if (this.state.plantIds.length <= 0) {
            return [];
        }
        return plantOptions;
    }

    loadPlantIds = () => {
        const uniquePlantIds = [];
        const plantDetails = [];
        ((this.props.userInstance && this.props.userInstance.props.PlantIds) || []).forEach((id, index) => {
            const plantInstance = PlantModel.getBy('PlantId', id);
            if (plantInstance) {
                uniquePlantIds.push(
                    plantInstance.props.UniqueId || plantInstance.props.id
                );
                plantDetails.push({
                    label: plantInstance.props.Plant_Region,
                    value: plantInstance.props.Plant_Region
                });
            }
        });
        const uniquePlantDetails = plantDetails.filter((thing, index, self) =>
            index === self.findIndex((t) => (
                t.value === thing.value && t.label === thing.label
            ))
        );
        dispatch(actions.change(`${this.props.formModelName}.Plants`, uniquePlantIds.join(',')));
        this.setState({
            plantDetails: uniquePlantDetails,
            plantIds: uniquePlantIds
        });
    }

    getDefaultPlants = (plantIds: string[]) => {
        const plantUniqueIds = [];
        const distinctPlantIds = this.state.plantIds.length > 0 ?
            this.state.plantIds.filter((plantId => plantIds.indexOf(plantId) < 0)) : plantIds;
        const allPlantIds = distinctPlantIds.concat(this.state.plantIds);
        allPlantIds.forEach(id => {
            const plantInstance = PlantModel.getBy('id', id) || PlantModel.getBy('PlantId', id);
            const plantRegions = this.state.plantDetails.map((detail) => detail.value);
            if (plantInstance && plantUniqueIds.indexOf(id) < 0 &&
                plantRegions.indexOf(plantInstance.props.Plant_Region) >= 0) {
                plantUniqueIds.push(plantInstance.props.id);
            }
        });
        if (this.state.plantIds.length <= 0) {
            return [];
        }
        return plantUniqueIds;
    }

    promise = async () => {
        await getAllPlantsByRegion(this.props.userId);
    }

    handleAccessLevel = (obj) => {
        if (obj && obj.value === 'L1') {
            dispatch(actions.reset(`${this.props.formModelName}.Plants`));
            this.setState({
                isNotL1User: false
            });
            return;
        }
        if (obj && obj.value !== 'L1') {
            this.setState({
                isNotL1User: true
            });
        }
    }

    handleRegionSelect = async (obj) => {
        const self = this;
        if (obj instanceof Array && obj.length > 0) {
            if (this.state.plantDetails.length > obj.length) {
                const previousRegions = this.state.plantDetails.map(details => { return details.value; });
                const newRegions = obj.map(details => { return details.value; });
                let removedRegion = '';
                previousRegions.some(region => {
                    if (newRegions.indexOf(region) <= -1) {
                        removedRegion = region;
                        return true;
                    }
                });
                const allPlantIds = (PlantModel.getAllBy('Plant_Region', removedRegion) || []).map((plantIns) => {
                    if (newRegions.indexOf(plantIns.props.Plant_Region) <= -1) {
                        return plantIns.props.id;
                    }
                });
                const remainingPlantIds = this.state.plantIds.filter((plantId) => {
                    return allPlantIds.indexOf(plantId) < 0;
                });

                this.setState({
                    plantIds: remainingPlantIds,
                    plantDetails: obj
                });
                dispatch(actions.change(`${this.props.formModelName}.Plants`, remainingPlantIds));
                removePlantOptions(removedRegion);
                return;
            }
            const plantDetailsData = await getPlantsByRegion(obj[obj.length - 1].value, this.props.userId);
            const plantOptions = [];
            const plantIds = this.state.plantIds;
            plantDetailsData.forEach((instance) => {
                plantOptions.push(instance);
                plantIds.push(instance.UniqueId);
            });
            dispatch(actions.change(`${this.props.formModelName}.Plants`, plantIds.join(',')));
            savePlantOptions(plantOptions);
            this.setState({
                plantIds,
                plantDetails: obj,
            });
        } else {
            this.setState({
                plantIds: [],
                plantOptions: []
            });
            emptyPlantOptions();
            dispatch(actions.change(`${this.props.formModelName}.Plants`, []));
        }
    }

    handlePlantSelect = (obj) => {
        const plantIds = [];
        if (obj && (this.state.plantIds.length > obj.length)) {
            dispatch(actions.reset(`${this.props.formModelName}.Plants`));
        }
        (obj || []).forEach(element => {
            plantIds.push(element.value);
            dispatch(actions.push(`${this.props.formModelName}.Plants`, element.value));
        });
        this.setState({ plantIds });
    }

    renderFormContent = () => {
        const { userInstance } = this.props;
        return (
            <React.Fragment>
                <Form
                    loadDefaultValues={this.loadPlantIds}
                    model={`${this.props.formModelName}`}
                    onSubmit={this.handleFormSubmit}
                    className="" >
                    {this.state.isLoading ? <Loader type="submission" /> : ''}
                    {this.props.id !== 'Update Form' ?
                        [<RRFInput
                            key={3}
                            model=".UserId"
                            type="text"
                            placeholder="Enter user id..."
                            id="UserId"
                            label="User ID:"
                            defaultValue={userInstance && userInstance.props.UserId.toString()}
                        />,
                        <RRFInput
                            key={4}
                            model=".Password"
                            type="password"
                            placeholder="Enter new password..."
                            id="Password"
                            label="Password:"
                        />] : <span />}
                    <RRFInput
                        model=".First_Name"
                        type="text"
                        placeholder="Enter first name..."
                        id="First_Name"
                        label="First Name:"
                        defaultValue={userInstance && userInstance.props.First_Name}
                    />
                    <RRFInput
                        model=".Middle_Name"
                        type="text"
                        placeholder="Enter middle name..."
                        id="Middle_Name"
                        label="Middle Name:"
                        defaultValue={userInstance && userInstance.props.Middle_Name}
                    />
                    <RRFInput
                        model=".Last_Name"
                        type="text"
                        placeholder="Enter last name..."
                        id="Last_Name"
                        label="Last Name:"
                        defaultValue={userInstance && userInstance.props.Last_Name}
                    />
                    <RRFInput
                        model=".Email"
                        type="email"
                        placeholder="Enter email address..."
                        id="Email"
                        label="Email:"
                        defaultValue={userInstance && userInstance.props.Email}
                    />
                    <RRFInput
                        model=".Access_Level"
                        type="dropdown"
                        placeholder="Select access level..."
                        id="Access_Level"
                        menuItems={[{ label: 'L1', value: 'L1' },
                        { label: 'L2', value: 'L2' },
                        { label: 'L3', value: 'L3' },
                        { label: 'L4', value: 'L4' },
                        ]}
                        label="Access Level:"
                        defaultValue={this.props.userInstance ? this.props.userInstance.props.Access_Level : ''}
                        onSelect={this.handleAccessLevel}
                    />
                    {this.state.isNotL1User ?
                        [<RRFInput
                            key={1}
                            model="forms.plantForm.Plant_Region"
                            type="dropdown"
                            placeholder="Select plant region..."
                            id="PlantRegion"
                            menuItems={[{
                                label: 'Arkansas', value: 'Arkansas'
                            },
                            {
                                label: 'DFW', value: 'DFW'
                            },
                            {
                                label: 'Houston', value: 'Houston'
                            }
                            ]}
                            onSelect={this.handleRegionSelect}
                            label="Plant Region:"
                            defaultValue={this.props.userInstance ?
                                this.props.userInstance.props.Regions.toString() : ''}
                            multi={true}
                        />,
                        <RRFInput
                            key={2}
                            model=".Plants"
                            type="dropdown"
                            placeholder="Select plants..."
                            id="Plants"
                            menuItems={this.getPlantOptions(this.props.userInstance &&
                                this.props.userInstance.props.PlantIds)}
                            multi={true}
                            label="Plants:"
                            onSelect={this.handlePlantSelect}
                            defaultValue={this.props.userInstance ?
                                this.getDefaultPlants(this.props.userInstance.props.PlantIds).toString() :
                                this.getDefaultPlants(this.state.plantIds).toString()}
                        />
                        ] : <span />
                    }
                    <RRFInput
                        model=".Job_Title"
                        type="text"
                        placeholder="Enter job title..."
                        id="Job_Title"
                        defaultValue={userInstance && userInstance.props.Job_Title}
                        label="Job Title:"
                    />
                    <RRFInput
                        model=".Job_Desc"
                        type="text"
                        placeholder="Enter job description..."
                        id="Job_Desc"
                        label="Job Description:"
                        defaultValue={userInstance && userInstance.props.Job_Desc}
                    />
                    <FormFooter />
                </Form>
            </React.Fragment>
        );
    }

    render() {
        return <Async
            identifier="UserForm"
            promise={this.promise}
            loader={<Loader type="async" />}
            error={<ErrorPage />}
            content={this.renderFormContent()}
        />;
    }
}

export function mapStateToProps(state) {
    const plantOptions = state.miscellaneous.get('plantOptions');
    const userId = state.login.get('UniqueId');
    return {
        state, plantOptions, userId
    };
}

export const UserForm = connect<{}, {}, IUserFormProps>(mapStateToProps)(UserFormImpl);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Users/UserForm.tsx